import React, { useEffect, useState, Fragment, useCallback, useMemo } from 'react';
import MUIAutocomplete from '@mui/material/Autocomplete';
import MUITextField from '@mui/material/TextField';
import MUICircularProgress from '@mui/material/CircularProgress';
import MuiTypography from '@mui/material/Typography';
import Error from './Error.component';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import MuiBox from '@mui/material/Box';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { fetchAPI } from '../../../api/request';
import _ from 'lodash';

const AsyncAutocomplete = ({
  name,
  required,
  api,
  label,
  disabled,
  autoFocus,
  dependsOn,
  query,
  queryBuilder,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const { jwt } = useSelector((state) => state.login);
  const [loading, setLoading] = useState(false);
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext();
  const watchedDependencyValues = useWatch({
    control,
    name: Array.isArray(dependsOn) ? dependsOn : dependsOn ? [dependsOn] : [],
  });

  const {
    field: { ref, onChange, value, ...inputProps },
  } = useController({ name: name, control: control, rules: { required } });
  const { from } = useSelector((state) => state.year);

  const path = useMemo(() => {
    return _.head(_.split(api?.path, '?'));
  }, [api?.path]);

  const queryParams = useMemo(() => {
    if (typeof queryBuilder === 'function') {
      return queryBuilder({
        values: getValues(),
        dependencies: watchedDependencyValues,
      }) || {};
    }
    return query || {};
  }, [queryBuilder, query, getValues, watchedDependencyValues]);

  const fetchData = useCallback(() => {
    setLoading(true);
    fetchAPI(api, {}, queryParams, {}, jwt)
      .then((response) => {
        setLoading(false);
        setOptions(response);
      })
      .catch(() => {
        setLoading(false);
        setOptions([]);
      });
  }, [api, jwt, queryParams]);

  useEffect(() => {
    if (path !== '/yachts')
      fetchData();
  }, [api, fetchData, path]);

  useEffect(() => {
    if (path === '/yachts')
      fetchData();
  }, [from, api, fetchData, value, path]);

  const displayOptions = useMemo(() => {
    if (value && options.length > 0) {
      const valueExists = options.some(item => item.id === value.id);
      if (!valueExists) {
        return [value, ...options];
      }
    }
    return options;
  }, [value, options]);

  return (
    <MuiBox display='flex' flexDirection={'column'}>
      <MUIAutocomplete
        {...inputProps}
        ref={ref}
        open={open}
        value={value || null}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        options={displayOptions}
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={(option, value) =>
          option.id === value.id
        }
        onChange={(e, data) => onChange(data)}
        filterOptions={(options, state) => {
          const inputValue = state.inputValue.toLowerCase();
          return options.filter((option) =>
            _.includes(option.name?.toLowerCase(), inputValue.toLowerCase())
          );
        }}
        renderOption={(props, option) => (
          <li {...props} key={option?.id}>
            <MuiBox display='flex' flexDirection='column'>
              <span>{option.name}</span>
              {option?.provenence ? (
                <MuiTypography variant='caption' color='text.secondary'>
                  {option.provenence}
                </MuiTypography>
              ) : null}
            </MuiBox>
          </li>
        )}
        loading={loading}
        renderInput={(params) => (
          <MUITextField
            {...params}
            label={label}
            required={required}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <Fragment>
                  {loading ? (
                    <MUICircularProgress color='inherit' size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </Fragment>
              ),
            }}
            error={!!errors[name]?.message}
            autoFocus={autoFocus || false}
          />
        )}
        disabled={disabled}
        {...props}
      />
      <Error error={errors[name]?.message} />
    </MuiBox>
  );
};

AsyncAutocomplete.propTypes = {
  name: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string,
  dependsOn: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  query: PropTypes.object,
  queryBuilder: PropTypes.func,
};

export default AsyncAutocomplete;